import React from 'react';
import { Skull, Beaker, Sparkles, ShieldCheck, FileCheck2, Waves } from 'lucide-react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../ui/hover-card';
import SectionLabel from '../ui-shree/SectionLabel';

/**
 * PurityInfographic — the editorial centrepiece for Western skeptics.
 *
 * Bridges "Visual Cleanliness" (the Sterility Trap) with "Biological
 * Purity" (the Shreesavaa Flow). Tagline: "Beyond the White: Verifying
 * the Soul of Purity."
 *
 * Visual: thin lines, glassmorphism, palette #C1D7E2–#90B0BF–#7C9CB1,
 * serif typography. Each flow step exposes a Dharma Audit hover-card
 * with the on-chain proof for that movement.
 */

const STERILITY_TRAP = [
  {
    title: 'Synthetic bleach',
    body: 'Chlorine and surfactant cocktails strip pigment, scent, and barrier lipids in one wipe.',
  },
  {
    title: 'Harsh mineral salts',
    body: 'Sulphates, parabens and EDTA: optical clarity bought at the cost of the skin microbiome.',
  },
  {
    title: 'Zero microbial life',
    body: 'Sterilised to the point of barrenness — the ecosystem on your skin is collateral damage.',
  },
  {
    title: 'Barrier disruption',
    body: 'Repeated use thins the acid mantle. The mirror reads "clean"; the body reads "wound".',
  },
];

const SHREESAVAA_FLOW = [
  {
    n: '01',
    icon: Beaker,
    title: 'The Source',
    eyebrow: 'Kewra Hydrosol',
    body:
      'Our base is captured from the sacrificed steam of Pandanus blossoms. We use the breath of the plant, not its blood — a circular essence, never plain water.',
    audit: {
      label: 'Dharma Audit · Source',
      lines: [
        'Origin: Kewra distillation, Tamil Nadu cooperative',
        'Steam-capture yield · 0.42 L per kg blossom',
        'Chain hash · 0x91f3…ae07',
      ],
    },
  },
  {
    n: '02',
    icon: Sparkles,
    title: 'The Ritual',
    eyebrow: '100 % Botanical Saponification',
    body:
      'Six cold-pressed oils marry our plant lye in a 28-day saponification rest. Harsh elements are converted, not buried — every caustic ion becomes a softening salt.',
    audit: {
      label: 'Dharma Audit · Ritual',
      lines: [
        'Saponification value matched to lipid blend',
        'Saponification rest · 28 days at 18–22 °C',
        'No synthetic surfactant introduced · audited',
      ],
    },
  },
  {
    n: '03',
    icon: ShieldCheck,
    title: 'The Proof',
    eyebrow: 'Ledger-Verified Data',
    body:
      'Every batch publishes a lab certificate on chain: 0 % residual lye, 0.0 % Free Alkali, microbial counts well below cosmetic-grade thresholds.',
    audit: {
      label: 'Dharma Audit · Proof',
      lines: [
        'Free Alkali · 0.00 % (target 0.00)',
        'pH · 7.8 – 8.4 (target 7.0–11.0)',
        'Microbial · ≤ 22 CFU / g (cap 100)',
        'Lab COA hashed on chain · per batch',
      ],
    },
  },
  {
    n: '04',
    icon: Waves,
    title: 'The Return',
    eyebrow: 'Sewer-Positive Impact',
    body:
      'Once rinsed, what remains is food. 100 % biodegradable molecules feed the wastewater microbiome — prebiotics for the rivers downstream.',
    audit: {
      label: 'Dharma Audit · Return',
      lines: [
        'OECD 301B biodegradability · ≥ 90 % @ 28 days',
        'Aquatic toxicity · NOEC > 100 mg/L',
        'No PFAS, no microplastics · declared & audited',
      ],
    },
  },
];

const PurityInfographic = () => (
  <section
    id="purity-infographic"
    data-testid="purity-infographic"
    className="relative px-6 md:px-12 py-24 md:py-32 bg-gradient-to-b from-[#F4F8F9] via-[#F4F8F9] to-[#C1D7E2]/40 overflow-hidden"
  >
    {/* Atmospheric glow */}
    <span
      aria-hidden
      className="absolute top-1/3 right-0 w-[520px] h-[520px] rounded-full blur-3xl opacity-40 pointer-events-none"
      style={{ background: 'radial-gradient(circle, #AAC6D4 0%, transparent 70%)' }}
    />

    <div className="relative max-w-[1400px] mx-auto">
      <SectionLabel testId="purity-infographic-label">
        Data-Backed Purity
      </SectionLabel>

      <h2
        data-testid="purity-infographic-tagline"
        className="font-serif text-5xl md:text-7xl font-light leading-[1.02] text-[#7C9CB1] max-w-4xl mb-6"
      >
        Beyond the White:
        <br />
        <em className="text-[#90B0BF]">Verifying the Soul of Purity.</em>
      </h2>
      <p className="font-serif italic text-xl md:text-2xl text-[#7C9CB1]/85 leading-relaxed max-w-3xl mb-20">
        The legacy industry chases visual cleanliness. We publish biological
        purity, on chain, batch by batch.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        {/* SECTION A · The Sterility Trap */}
        <SterilityTrap />

        {/* Vertical divider on desktop */}
        <div
          aria-hidden
          className="hidden lg:flex lg:col-span-1 h-full items-center justify-center"
        >
          <div className="w-px h-full bg-gradient-to-b from-transparent via-[#90B0BF]/40 to-transparent" />
        </div>

        {/* SECTION B · The Shreesavaa Flow */}
        <ShreesavaaFlow />
      </div>

      {/* Footer signature */}
      <div
        data-testid="purity-infographic-signature"
        className="mt-24 border-t border-[#C1D7E2] pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF]">
          Hover any flow node · Dharma Audit reveals on-chain proof
        </p>
        <p className="font-serif italic text-[#7C9CB1]">
          Verified ledger · Lots 042 · 043 · 044 — and every lot to come.
        </p>
      </div>
    </div>
  </section>
);

const SterilityTrap = () => (
  <div
    data-testid="sterility-trap"
    className="lg:col-span-5 relative"
  >
    <div className="border border-[#90B0BF]/40 bg-white/40 backdrop-blur-md p-8 md:p-10 relative">
      <div className="flex items-start gap-3 mb-6">
        <Skull size={22} strokeWidth={1.2} className="text-[#90B0BF] mt-1" />
        <div>
          <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF]">
            Section A · The Sterility Trap
          </p>
          <h3 className="mt-2 font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-tight">
            Visual cleanliness, biological cost.
          </h3>
        </div>
      </div>
      <p className="font-serif italic text-[#7C9CB1]/80 leading-relaxed mb-8">
        The legacy beauty industry confuses appearance with health. A bottle
        looks &ldquo;clean&rdquo; because everything alive in it has been
        killed.
      </p>
      <ul className="space-y-5">
        {STERILITY_TRAP.map((item, i) => (
          <li
            key={item.title}
            data-testid={`sterility-trap-item-${i}`}
            className="flex items-start gap-4 border-t border-[#90B0BF]/20 pt-5 first:border-t-0 first:pt-0"
          >
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF] pt-1 min-w-[22px]">
              ✕
            </span>
            <div>
              <p className="font-serif text-lg text-[#7C9CB1] leading-tight">
                {item.title}
              </p>
              <p className="text-sm text-[#7C9CB1]/75 font-light leading-relaxed mt-1">
                {item.body}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const ShreesavaaFlow = () => (
  <div data-testid="shreesavaa-flow" className="lg:col-span-6 relative">
    <div className="flex items-start gap-3 mb-10">
      <FileCheck2 size={22} strokeWidth={1.2} className="text-[#7C9CB1] mt-1" />
      <div>
        <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#7C9CB1]">
          Section B · The Shreesavaa Flow
        </p>
        <h3 className="mt-2 font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-tight">
          Deep purity, signed at every step.
        </h3>
      </div>
    </div>

    {/* Vertical timeline */}
    <ol className="relative pl-12 md:pl-16 border-l border-dashed border-[#90B0BF]/50 space-y-12">
      {SHREESAVAA_FLOW.map((step, i) => (
        <FlowNode key={step.n} step={step} index={i} />
      ))}
    </ol>
  </div>
);

const FlowNode = ({ step, index }) => {
  const Icon = step.icon;
  return (
    <li data-testid={`flow-node-${index}`} className="relative group">
      {/* Dot on the timeline */}
      <span
        aria-hidden
        className="absolute -left-[57px] md:-left-[73px] top-2 flex items-center justify-center w-12 h-12 rounded-full bg-[#F4F8F9] border border-[#7C9CB1] shadow-[0_4px_24px_-6px_rgba(124,156,177,0.4)] transition-transform duration-500 group-hover:scale-110"
      >
        <Icon size={18} strokeWidth={1.3} className="text-[#7C9CB1]" />
      </span>

      <HoverCard openDelay={120} closeDelay={80}>
        <HoverCardTrigger asChild>
          <button
            type="button"
            data-testid={`flow-node-trigger-${index}`}
            className="text-left w-full bg-[#F4F8F9]/60 backdrop-blur-md border border-[#C1D7E2] hover:border-[#7C9CB1] transition-colors p-6 md:p-7 cursor-help focus:outline-none focus:ring-2 focus:ring-[#7C9CB1]/40"
          >
            <div className="flex items-baseline justify-between gap-4 mb-3">
              <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#90B0BF]">
                Flow {step.n} · {step.eyebrow}
              </p>
              <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1]/70 hidden md:inline">
                hover · audit
              </span>
            </div>
            <h4 className="font-serif text-2xl md:text-3xl text-[#7C9CB1] leading-tight mb-3">
              {step.title}
            </h4>
            <p className="text-[#7C9CB1]/85 font-light leading-relaxed">
              {step.body}
            </p>
          </button>
        </HoverCardTrigger>

        <HoverCardContent
          side="right"
          align="start"
          sideOffset={16}
          data-testid={`flow-node-audit-${index}`}
          className="w-80 border-[#7C9CB1] bg-[#7C9CB1] text-[#F4F8F9] p-5 rounded-none"
        >
          <p className="font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/80 mb-3">
            {step.audit.label}
          </p>
          <ul className="space-y-2">
            {step.audit.lines.map((line, j) => (
              <li
                key={j}
                className="flex items-start gap-2 font-mono text-[11px] leading-relaxed text-[#F4F8F9]/95"
              >
                <span className="text-[#C1D7E2]">›</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </HoverCardContent>
      </HoverCard>
    </li>
  );
};

export default PurityInfographic;
